import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMyBlocks, unblockUser } from "@/lib/user-blocks.functions";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/account/blocks")({
  head: () => ({ meta: [{ title: "Blocked users — Account" }] }),
  component: BlocksPage,
});

function BlocksPage() {
  const listFn = useServerFn(listMyBlocks);
  const unblockFn = useServerFn(unblockUser);
  const { data: rows = [], refetch } = useQuery({ queryKey: ["my-blocks"], queryFn: () => listFn({}) });

  async function unblock(id: string) {
    const res = await unblockFn({ data: { blocked_id: id } });
    if ("error" in res) toast.error(res.error); else { toast.success("Unblocked"); refetch(); }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-6">
      <BackToDashboard />
      <header>
        <p className="text-[0.6rem] tracking-wide-luxury uppercase text-accent">Account</p>
        <h1 className="text-3xl font-medium">Blocked users</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Blocked users won't see your shifts or profile in the marketplace, and you won't see theirs. Blocks work in both directions.
        </p>
      </header>

      {rows.length === 0 ? (
        <div className="border border-border p-8 text-center text-sm text-muted-foreground">
          You haven't blocked anyone.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-3 border border-border p-3">
              <Avatar className="size-10">
                {r.avatar_url && <AvatarImage src={r.avatar_url} alt="" />}
                <AvatarFallback>{(r.display_name ?? "?").slice(0, 2)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">{r.display_name ?? "Unknown user"}</p>
                {r.reason && <p className="text-xs text-muted-foreground">Reason: {r.reason}</p>}
                <p className="text-xs text-muted-foreground">Blocked {new Date(r.created_at).toLocaleDateString("en-GB")}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => unblock(r.blocked_id)}>Unblock</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
